package features.booking

import kotlinx.serialization.Serializable

// Información enriquecida de parada/bajada o punto de subida con ciudad, coordenadas y horario
@Serializable
data class ParadaInfo(
    val nombre: String = "",
    val ciudad: String = "",   // ciudad del catálogo a la que pertenece
    val lat: Double = 0.0,
    val lng: Double = 0.0,
    val hora: String = "",     // horario asignado (solo para puntos de subida, ej: "06:00 AM")
    // Días en que opera este punto: TipoDia.TODOS / SEMANA / SABADO.
    // Vacío = registros viejos, se tratan como TODOS.
    val dias: String = ""
)

@Serializable
data class PasajeroInfo(
    val asiento: Int = 0,
    val nombre: String = "",
    val telefono: String = "",
    val correo: String = "",
    val parada: String = "",
    val maletasExtras: Int = 0,
    val llevaMascota: Boolean = false
) {
    companion object {
        /**
         * Parser centralizado para leer PasajeroInfo desde un DataSnapshot de Firebase.
         * Evita duplicar el mismo try/catch por cada campo en todo el repositorio.
         */
        fun fromSnapshot(snap: dev.gitlive.firebase.database.DataSnapshot): PasajeroInfo {
            return PasajeroInfo(
                asiento = try { snap.child("asiento").value<Int>() } catch (e: Exception) { 0 },
                nombre = try { snap.child("nombre").value<String>() } catch (e: Exception) { "" },
                telefono = try { snap.child("telefono").value<String>() } catch (e: Exception) { "" },
                correo = try { snap.child("correo").value<String>() } catch (e: Exception) { "" },
                parada = try { snap.child("parada").value<String>() } catch (e: Exception) { "" },
                maletasExtras = try { snap.child("maletasExtras").value<Int>() } catch (e: Exception) { 0 },
                llevaMascota = try { snap.child("llevaMascota").value<Boolean>() } catch (e: Exception) { false }
            )
        }

        /** Serializa una lista de PasajeroInfo a JsonArray string para enviar a Cloud Functions */
        fun listaAJsonString(pasajeros: Collection<PasajeroInfo>): String {
            val arr = kotlinx.serialization.json.buildJsonArray {
                pasajeros.forEach { p ->
                    add(kotlinx.serialization.json.buildJsonObject {
                        put("nombre", kotlinx.serialization.json.JsonPrimitive(p.nombre))
                        put("asiento", kotlinx.serialization.json.JsonPrimitive(p.asiento))
                        put("telefono", kotlinx.serialization.json.JsonPrimitive(p.telefono))
                        put("correo", kotlinx.serialization.json.JsonPrimitive(p.correo))
                        put("parada", kotlinx.serialization.json.JsonPrimitive(p.parada))
                        put("maletasExtras", kotlinx.serialization.json.JsonPrimitive(p.maletasExtras))
                        put("llevaMascota", kotlinx.serialization.json.JsonPrimitive(p.llevaMascota))
                    })
                }
            }
            return arr.toString()
        }
    }
}

@Serializable
data class Boleto(
    val asientos: List<Int> = emptyList(),
    val pasajeros: List<PasajeroInfo> = emptyList(),
    val totalPagado: Int = 0,
    val fechaViaje: String = "",
    val horario: String = "",
    val origenDestino: String = "",
    val puntoSubida: String = "",
    // Datos que el correo sí pinta y el boleto descargable debe igualar (ver BoletoHtml.kt).
    // codigoQR vacío = se reconstruye desde timestamp+asiento.
    val codigoQR: String = "",
    val horaSubida: String = "",
    val mapsUrl: String = "",
    val mapsUrlBajada: String = "",
    val metodoPago: String = "",
    val pagoConfirmado: Boolean = true,
    val timestamp: String = "0",
    val urlSalida: String = "",
    val esIdaYVuelta: Boolean = false,
    val fechaVuelta: String = "",
    val horarioVuelta: String = "",
    val rutaVuelta: String = "",
    // Tramo de VUELTA: asientos y pasajeros propios (pueden diferir de la ida)
    val asientosVuelta: List<Int> = emptyList(),
    val pasajerosVuelta: List<PasajeroInfo> = emptyList(),
    val puntoSubidaVuelta: String = "",
    val requiereFactura: Boolean = false,
    val rfc: String = "",
    val razonSocial: String = "",
    val regimenFiscal: String = "",
    val usoCfdi: String = "",
    val cpFiscal: String = ""
) {
    val timestampLong: Long get() = timestamp.toLongOrNull() ?: 0L
}
@Serializable
data class DatosDia(
    val reservas: Map<String, Boleto> = emptyMap()
)

@Serializable
data class ResetResult(
    val success: Boolean = false,
    val tempPassword: String? = null,
    val error: String? = null
)

@Serializable
data class ResetResponse(
    val result: ResetResult = ResetResult()
)

@Serializable
data class ReservaAdmin(
    val idReserva: String,
    val ruta: String,
    val fecha: String,
    val horario: String,
    val asientosComprados: Int,
    val totalPagado: Int,
    val esPasado: Boolean = false,
    val viajeFueCancelado: Boolean = false,
    val pasajeros: List<PasajeroInfo> = emptyList(),
    val metodoPago: String = "",
    val pagoConfirmado: Boolean = true,
    val puntoSubida: String = ""
)

// ═══ Configuración de vehículos con layout de asientos ═══

/**
 * Representa una fila del vehículo.
 * columnas = 4 siempre (grid de 4 cols: izq|pasillo|pasillo|der o izq|centro|pasillo|der)
 * asientos = lista de los IDs de asiento en cada columna (0 = vacío/pasillo/chofer)
 * Ejemplo fila chofer:   asientos = [-1, 0, 0, 3]  → -1=volante, 0=vacío, 0=vacío, 3=asiento 3
 * Ejemplo fila normal:   asientos = [1, 2, 0, 4]   → asiento, asiento, pasillo, asiento
 */
@Serializable
data class FilaVehiculo(
    val asientos: List<Int> = emptyList() // -1 = volante, 0 = vacío/pasillo, >0 = número de asiento
)

@Serializable
data class VehiculoConfig(
    val id: String = "",
    val nombre: String = "",       // "Sprinter 2025 (ABC-456)"
    val marca: String = "",
    val modelo: String = "",
    val placas: String = "",
    val capacidad: Int = 13,       // total de asientos vendibles
    val columnas: Int = 4,         // ancho del grid
    val filas: List<FilaVehiculo> = emptyList()  // layout completo
)

enum class AdminSeatStatus {
    LIBRE,        // Verde - nadie lo tiene
    PAGADO,       // Gris - pago verificado/confirmado
    CONGELADO     // Amarillo - pago pendiente (SPEI/Oxxo en proceso)
}

data class AdminSeatInfo(
    val asiento: Int,
    val status: AdminSeatStatus = AdminSeatStatus.LIBRE,
    val pasajero: PasajeroInfo? = null,
    val metodoPago: String = "",
    val expiraEn: Long = 0L,
    val paymentIntentId: String = ""
)

data class ConflictoAsiento(
    val id: String = "",
    val paymentIntentId: String = "",
    val ruta: String = "",
    val fecha: String = "",
    val horario: String = "",
    val metodoPago: String = "",
    val email: String = "",
    val montoTotal: Int = 0,
    val montoPendiente: Int = 0,
    val asientosAsignados: List<Int> = emptyList(),
    val asientosEnConflicto: List<Int> = emptyList(),
    val pasajerosEnConflicto: List<PasajeroInfo> = emptyList(),
    val pasajerosAsignados: List<PasajeroInfo> = emptyList(),
    val status: String = "",      // pendiente, reasignado, reembolsado
    val timestamp: Long = 0L,
    val reservaIdParcial: String = ""
)

data class PagoRegistro(
    val paymentIntentId: String = "",
    val ruta: String = "",
    val fecha: String = "",
    val horario: String = "",
    val asientos: String = "",
    val monto: Int = 0,
    val timestamp: Long = 0L,
    val status: String = "",         // pendiente, confirmado, conflicto, expirado
    val metodoPago: String = "",     // spei, oxxo, tarjeta, efectivo
    val email: String = "",
    val clabe: String = "",          // SPEI
    val oxxoNumber: String = "",     // Oxxo
    val voucherUrl: String = "",
    val requiereAtencion: Boolean = false,
    val motivo: String = "",
    val pasajeros: List<PasajeroInfo> = emptyList(),
    val reservaId: String = ""
)

// ═══ Modelos Admin ═══

data class AlertaActividad(
    val icono: String,
    val titulo: String,
    val descripcion: String,
    val color: androidx.compose.ui.graphics.Color
)

data class ViajeActivo(
    val ruta: String,
    val fecha: String,
    val horario: String,
    val chofer: String,
    val vehiculo: String,
    val vendidos: Int = 0,
    val capacidad: Int = 0
) {
    val disponibles: Int get() = (capacidad - vendidos).coerceAtLeast(0)
}

data class PagoManual(
    val id: String = "",
    val ruta: String = "",
    val fecha: String = "",
    val horario: String = "",
    val asientos: String = "",
    val email: String = "",
    val totalPagado: Int = 0,
    val pasajeros: String = "",
    val timestamp: Long = 0L,
    val estatus: String = "pendiente", // pendiente, confirmado, declinado
    val puntoSubida: String = "",
    val resueltoPorNombre: String = ""
)

data class SolicitudFactura(
    val id: String = "",
    val rfc: String = "",
    val razonSocial: String = "",
    val regimenFiscal: String = "",
    val usoCfdi: String = "",
    val cpFiscal: String = "",
    val email: String = "",
    val monto: Int = 0,
    val ruta: String = "",
    val fecha: String = "",
    val horario: String = "",
    val asientos: String = "",
    val timestamp: Long = 0L,
    val estatus: String = "pendiente", // pendiente, enviada, facturada
    val paymentIntentId: String = ""   // referencia del pago (Stripe/manual) para conciliar
)

data class ResultadoBusqueda(
    val reserva: ReservaAdmin,
    val pasajero: PasajeroInfo,
    val puntoSubida: String = ""
)